import React, { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { ColumnConfig, SortDirections } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import OperationsService from "@app/operations/operationsService";
import JobQueueService  from "@app/jobs/job-queues/jobQueueService";
import { WeekendRunFailedJobsResult } from "@app/operations/jobStatisticsResult";
import { JobQueue } from "@app/jobs/job-queues/jobQueue";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { sortByDate } from "@app/shared/sortByDate";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import ProjectService from "@app/projects/projectService";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESButton } from "@app/shared/qes-standards/Button";
import { getMostRecentSaturday, getMostRecentWednesday } from "@app/shared/utility/getMostRecentDay";

interface WeekendRunFailedJobsProps {
  clients: { clientId: number; name: string }[];
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}

const operationsService = new OperationsService();
const projectService = new ProjectService();
const jobQueueService = new JobQueueService();
const WeekendRunFailedJobsColumns: ColumnConfig = {
  /* oxlint-disable sort-keys */
  jobId: {
    displayText: "Job Id",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__job-id",
  },
  jobType: {
    displayText: "Job Type",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__job-type",
  },
  regionSetId: {
    displayText: "Region Set Id",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__region-set-id",
  },
  regionCode: {
    displayText: "Region Code",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__region-code",
  },
  projectId: {
    displayText: "Project Id",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__project-id",
  },
  projectRunConfigurationId: {
    displayText: "Project Run Configuration Id",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__project-run-configuration-id",
  },
  dateFinished: {
    displayText: "Date Finished",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__date-finished",
  },
  errorMessage: {
    displayText: "Error Message",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__error-message",
  },
  jobQueueName: {
    displayText: "Job Queue Name",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__job-queue-name",
  },
  jobQueueSize: {
    displayText: "Job Queue Size",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__job-queue-size",
  },
  correlatedSuccessfulJobId: {
    displayText: "Correlated Successful Job Id",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__correlated-successful-job-id",
  },
  correlatedRunningJobId: {
    displayText: "Correlated Running Job Id",
    sizeContentToFit: true,
    testId: "weekend-run-failed-jobs__correlated-running-job-id",
  }
  /* oxlint-enable sort-keys */
};

const WeekendRunFailedJobs: React.FC<WeekendRunFailedJobsProps> = ({ clients, clientId, setClientId }) => {
  const isHumana = clients.find((c) => c.clientId === clientId)?.name === "Humana";
  const isUHC = clients.find((c) => c.clientId === clientId)?.name === "United HealthCare Services";
  const [weekendRunFailedJobsStats, setWeekendRunFailedJobsStats] = useState<WeekendRunFailedJobsResult[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [runDate, setRunDate] = useState<string>(isHumana ? getMostRecentSaturday() : getMostRecentWednesday());
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [newQueueName, setNewQueueName] = useState("");
  const [jobQueueList, setJobQueueList] = useState<JobQueue[]>([]);

  const PROJECT_SELECT_ERROR_TEXT = "Disabled";

  useEffect(() => {
    const getQueueList = async () => {
      const queueList = await jobQueueService.getJobQueues()
      setJobQueueList(queueList);
    }
    getQueueList();
    getProjectFailedJobStatuses();
  }, []);

  useEffect(() => {
    resetFormValues();
  }, [clientId]);

  useEffect(() => {
    if (selectedProjects.length === 1) {
      const idIndex = Number(selectedProjects[0]);
      const selectedProject = weekendRunFailedJobsStats[idIndex];
      setNewQueueName(selectedProject.jobQueueName);
      setJobQueueList((jobQueueList.sort(a => a.name === newQueueName ? -1 : 1)));
    }
    else {
      setNewQueueName("");
    }
  }, [selectedProjects, newQueueName, jobQueueList]);

  const resetFormValues = () => {
    setError(null);
    setWeekendRunFailedJobsStats(null);
    setSelectedProjects([]);
  };

  const getProjectFailedJobStatuses = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelectedProjects([]);
    try {
      if (!!clientId && !!runDate) {
        const response = await operationsService.getFailedJobsByClient(
          clientId,
          runDate
        );
        setWeekendRunFailedJobsStats(response);
      }
    } catch (error) {
      setError("Error: Incorrect Date or Client");
    } finally {
      setLoading(false);
    }
  }, [clientId, runDate, weekendRunFailedJobsStats]);

  const requeueProjectButtonHandler = useCallback(async () => {
    const idIndex = Number(selectedProjects[0]);
    const selectedProject = weekendRunFailedJobsStats[idIndex];
    setLoading(true);
    setWeekendRunFailedJobsStats(null);

    try {
      await projectService.resubmitProjectJobForClient(
        isHumana,
        isUHC,
        selectedProject.projectId,
        selectedProject.projectRunConfigurationId,
        selectedProject.jobId,
        newQueueName);

      toast("Job Requeued",
        {
          containerId: "z-index-9999",
          type: "success",
        }
      )
    }
    catch (error) {
      setError("Error: Failed to Resubmit Job");
    }
    finally {
      setSelectedProjects([]);
      setLoading(false);
      getProjectFailedJobStatuses();
    }
  }, [selectedProjects, newQueueName, weekendRunFailedJobsStats, getProjectFailedJobStatuses, isHumana, isUHC]);

  const tableData = useMemo(() => {
    let data: WeekendRunFailedJobsResult[] = [];
    if (weekendRunFailedJobsStats) {
      data = weekendRunFailedJobsStats.map((stat, index) => ({
        /* oxlint-disable sort-keys */
        key: index.toString(),
        jobId: stat.jobId,
        jobType: stat.jobType,
        regionSetId: stat.regionSetId,
        regionCode: stat.regionCode,
        projectId: stat.projectId,
        projectRunConfigurationId: stat.projectRunConfigurationId,
        dateFinished: stat.dateFinished,
        errorMessage: stat.errorMessage,
        jobQueueName: stat.jobQueueName,
        jobQueueSize: stat.jobQueueSize,
        dateCreated: stat.dateCreated,
        correlatedSuccessfulJobId: stat.correlatedSuccessfulJobId,
        correlatedRunningJobId: stat.correlatedRunningJobId,
        checkBoxIsDisabled: !!stat.correlatedSuccessfulJobId || !!stat.correlatedRunningJobId || !stat.projectId || !stat.projectRunConfigurationId
        /* oxlint-enable sort-keys */
      }));
    }
    return data;
  }, [weekendRunFailedJobsStats]);


  const handleSort = (records: WeekendRunFailedJobsResult[], column: keyof WeekendRunFailedJobsResult, direction: SortDirections) => {
    const data = [...records];
    if (direction === SortDirections.None) {
      return data;
    }

    const multiplier = direction === SortDirections.Asc ? 1 : -1;

    switch (column) {
      case "dateCreated":
        return sortByDate(data, "dateCreated", direction, "projectId");
      case "dateFinished":
        return sortByDate(data, "dateFinished", direction, "projectId");
      default:
        return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
    }
  }

  const isClientSelectionInvalid = clientId === null;

  let calculateDisabledTooltipText = "";
  if (!runDate || !clientId) {
    calculateDisabledTooltipText = PROJECT_SELECT_ERROR_TEXT;
  }
  let calculateDisabledTooltipRequeueText = "";
  if (loading || selectedProjects.length === 0 || weekendRunFailedJobsStats === null || selectedProjects.length > 1) {
    calculateDisabledTooltipRequeueText = PROJECT_SELECT_ERROR_TEXT;
  }

  return (
    <div>
      <div className="form-row mb-3">
        <div className="col pe-1">
          <QESSelect
            label="Client"
            id="clientSelect"
            value={clientId ?? ""}
            onChange={(event) => setClientId(Number(event.target.value))}
            placeholder={clients.length === 0 ? "Loading Clients..." : "Select Client"}
            testId="weekend-run-failed-jobs__client-select"
            required
            disabled={clients.length === 0}
            isValid={!isClientSelectionInvalid}
            errorText={"Invalid Client"}
            positionDescriptionTextAbsolutely={true}
          >
            {clients && clients.map((client) => (
              <option key={client.clientId} value={client.clientId.toString()}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        <div className="col px-1">
          <QESDateInput
            label="Run Date"
            value={runDate}
            onChange={(event) => setRunDate(event.target.value)}
            testId="weekend-run-failed-jobs__run-date-input"
          />
         </div>
         <div className="col d-flex align-items-end ps-1">
           <TooltipWrapper position="top" text={calculateDisabledTooltipText}>
             <QESButton
               disabled={!runDate || !clientId}
              onClick={getProjectFailedJobStatuses}
               qesType="primary"
               qesTestId="weekend-run-failed-jobs-statuses__get-failed-jobs-results"
             >
               Search
             </QESButton>
           </TooltipWrapper>
         </div>
       </div>
       <div className="row">
         <div className="col-auto p-0">
           <TooltipWrapper position="top" text={calculateDisabledTooltipRequeueText}>
             <QESButton
               disabled={loading || selectedProjects.length === 0 || weekendRunFailedJobsStats === null || selectedProjects.length > 1}
               onClick={requeueProjectButtonHandler}
               className="ms-2 me-1 mt-1"
               qesTestId={"jobs-list__requeue-job-button"}>Requeue Job</QESButton>
           </TooltipWrapper>
         </div>
         <div className="col-auto py-0 ps-1 pe-0">
           <QESSelect
             allowEmpty={true}
             containerClassName="jobs-list__requeue-job-queue-list"
             testId="jobs-list__requeue-list"
             label=""
             placeholder="Select a Queue"
             onChange={(e) => setNewQueueName(e.target.value)}
             disabled={selectedProjects.length !== 1 || weekendRunFailedJobsStats === null}
             value={newQueueName} >
             {jobQueueList.map(item => (
               <option key={item.id} value={item.name}>
                 {item.name}
               </option>
             ))}
           </QESSelect>
         </div>
       </div>
      <div className="mt-4">
        {loading && <LoadingSpinner />}
        {error && <div>{error}</div>}
        {weekendRunFailedJobsStats && (
          <Waffles
            columns={WeekendRunFailedJobsColumns}
            customSort={handleSort}
            data={tableData}
            enablePagination={false}
            testId="weekend-run-failed-jobs__failed-jobs-table"
            showCheckboxes={true}
            handleCheckboxChange={setSelectedProjects}
            selectedKeys={selectedProjects}
          />
        )}
      </div>
    </div>
  );
};
export default WeekendRunFailedJobs;